import { BellAlert, BuildingStorefront, ChevronRight, Clock, Key, ShieldCheck, Users } from "@medusajs/icons"
import { Button, Input, Label, Text, toast } from "@medusajs/ui"
import { useState, type ComponentType, type FormEvent } from "react"
import { Link } from "react-router-dom"
import { PageHeader, Section } from "@/components/ui"
import { useChangePassword, useUpdateMe } from "@/lib/api"
import { useCan, useCurrentMe } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import type { Permission } from "@/lib/types"

type Entry = { to: string; title: string; body: string; icon: ComponentType<any>; perm?: Permission }

const ENTRIES: Entry[] = [
  { to: "/settings/account", title: "Your account", body: "Your name and password", icon: Key },
  { to: "/settings/notifications/staff", title: "Your notifications", body: "Alerts on your phone and computer: new orders, security changes, stock, problems. Send a test.", icon: BellAlert },
  { to: "/settings/users", title: "Users and permissions", body: "Store owner, staff accounts and what each can do", icon: Users, perm: "staff:read" },
  { to: "/settings/activity", title: "Activity log", body: "Every change staff made, newest first", icon: Clock, perm: "logs:read" },
  { to: "/settings/security-log", title: "Security log", body: "Sign-ins, payment attempts, blocked scripts and every change with before and after values", icon: ShieldCheck, perm: "logs:read" },
  { to: "/settings/store", title: "Store details", body: "Name, contact, shipping, taxes and payments", icon: BuildingStorefront, perm: "settings:read" },
]

/** Settings home, laid out like Shopify's settings list. */
export function SettingsPage() {
  const can = useCan()
  return (
    <>
      <PageHeader title="Settings" />
      <Section>
        {ENTRIES.filter((e) => !e.perm || can(e.perm)).map((e) => {
          const Icon = e.icon
          return (
            <Link key={e.to} to={e.to} className="flex items-center gap-x-4 px-4 py-4 transition-colors hover:bg-ui-bg-base-hover">
              <div className="flex size-9 items-center justify-center rounded-lg border border-ui-border-base bg-ui-bg-subtle">
                <Icon className="text-ui-fg-subtle" />
              </div>
              <div className="flex-1">
                <Text size="small" weight="plus">
                  {e.title}
                </Text>
                <Text size="small" className="text-ui-fg-subtle">
                  {e.body}
                </Text>
              </div>
              <ChevronRight className="text-ui-fg-muted" />
            </Link>
          )
        })}
      </Section>
    </>
  )
}

import { TwoFactorCard } from "@/components/two-factor"

export function AccountPage() {
  const me = useCurrentMe()
  const updateMe = useUpdateMe()
  const changePassword = useChangePassword()
  const [first, setFirst] = useState(me.user.first_name ?? "")
  const [last, setLast] = useState(me.user.last_name ?? "")
  const [current, setCurrent] = useState("")
  const [next, setNext] = useState("")
  const [confirm, setConfirm] = useState("")

  const saveProfile = (e: FormEvent) => {
    e.preventDefault()
    updateMe.mutate({ first_name: first, last_name: last }, { onSuccess: () => toast.success("Profile saved"), onError: (err) => toast.error(errorMessage(err)) })
  }
  const savePassword = (e: FormEvent) => {
    e.preventDefault()
    if (next !== confirm) return toast.error("The new passwords don't match.")
    changePassword.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: () => {
          toast.success("Password changed. Other sessions were signed out.")
          setCurrent("")
          setNext("")
          setConfirm("")
        },
        onError: (err) => toast.error(errorMessage(err)),
      }
    )
  }

  return (
    <>
      <PageHeader title="Your account" subtitle={`${me.user.email} · ${me.staff?.role === "owner" ? "Store owner" : me.staff?.role === "admin" ? "Full permissions" : "Staff"}`}  />
      <div className="flex max-w-2xl flex-col gap-y-4">
        <Section title="Profile">
          <form onSubmit={saveProfile} className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-2">
            <div className="flex flex-col gap-y-1.5">
              <Label htmlFor="first">First name</Label>
              <Input id="first" value={first} onChange={(e) => setFirst(e.target.value)} />
            </div>
            <div className="flex flex-col gap-y-1.5">
              <Label htmlFor="last">Last name</Label>
              <Input id="last" value={last} onChange={(e) => setLast(e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Button type="submit" size="small" variant="secondary" isLoading={updateMe.isPending}>
                Save profile
              </Button>
            </div>
          </form>
        </Section>
        <Section title="Password">
          <form onSubmit={savePassword} className="flex flex-col gap-y-4 px-4 py-4">
            <div className="flex flex-col gap-y-1.5">
              <Label htmlFor="current">Current password</Label>
              <Input id="current" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-y-1.5">
                <Label htmlFor="new">New password</Label>
                <Input id="new" type="password" autoComplete="new-password" minLength={8} required value={next} onChange={(e) => setNext(e.target.value)} />
              </div>
              <div className="flex flex-col gap-y-1.5">
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input id="confirm" type="password" autoComplete="new-password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
            </div>
            <Text size="xsmall" className="text-ui-fg-subtle">
              At least 8 characters. Changing it signs you out on your other devices.
            </Text>
            <div>
              <Button type="submit" size="small" variant="secondary" isLoading={changePassword.isPending}>
                Change password
              </Button>
            </div>
          </form>
        </Section>
        <TwoFactorCard />
      </div>
    </>
  )
}
